'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { VariantProps } from 'tailwind-variants';

import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitCheckboxBase } from '@/fo/ui/kit/checkbox/base';
import { UiKitInputBase } from '@/fo/ui/kit/input/base';
import { staticMediaUrl } from '@/fo/util/image-loader';

import { Claims } from '../components/Claims/Claims';

import { styles } from './UiSubscriptionSectionBase.styles';

type Variants = VariantProps<typeof styles>;

export type SubscriptionFormStatus = Variants['formStatus'];

export interface BrandImage {
  /** URL изображения */
  src: string;
  /** Альтернативный текст для изображения */
  alt: string;
}

export interface UiSubscriptionSectionBaseProps {
  /** Статус авторизации пользователя */
  loginStatus?: Variants['loginStatus'];
  /** Статус формы подписки */
  formStatus?: SubscriptionFormStatus;
  /** Процент скидки для отображения, по умолчанию 15 */
  discountPercentage?: number;
  /** Изображение бренда, по умолчанию BIODERMA-BRAND-BOOK.png */
  brandImage?: BrandImage;
  /** Функция для подписки */
  onSubscribe?: (email: string) => Promise<void>;
  /** Текст ответа */
  errorText?: string;
  isSubscribed: boolean;
  /** Только блок УТП без формы подписки */
  claimsOnly?: boolean;
}

/**
 * Компонент секции подписки с формой для сбора email и отображением скидки
 */
export function UiSubscriptionSectionBase({
  loginStatus,
  formStatus,
  onSubscribe,
  brandImage = {
    src: staticMediaUrl('section/subscription/BIODERMA-BRAND-BOOK.png'),
    alt: 'BIODERMA-BRAND-BOOK'
  },
  discountPercentage = 15,
  errorText,
  isSubscribed,
  claimsOnly = false,
  ...props
}: UiSubscriptionSectionBaseProps) {
  const t = useTranslations();
  const showForm = !isSubscribed && !claimsOnly;
  const slots = styles({
    loginStatus,
    formStatus,
    isSubscribed: isSubscribed || claimsOnly
  });
  const [email, setEmail] = useState('');
  const [newsletterApproved, setNewsletterApproved] = useState(false);
  const [personalDataApproved, setPersonalDataApproved] = useState(false);
  const [newsletterError, setNewsletterError] = useState(false);
  const [personalDataError, setPersonalDataError] = useState(false);

  const onChangeEmail = (value: string) => {
    setEmail(value);
  };
  const onClickSubscribe = async () => {
    const newsletterOk = newsletterApproved;
    const personalOk = personalDataApproved;
    setNewsletterError(!newsletterOk);
    setPersonalDataError(!personalOk);
    if (!newsletterOk || !personalOk) {
      return;
    }
    await onSubscribe?.(email);
  };

  return (
    <div
      className={slots.base()}
      data-testid="subscription-section"
      data-mobile-app-hidden="subscription"
      {...props}
    >
      <Claims
        className={slots.claims()}
        isSubscribed={isSubscribed || claimsOnly}
      />
      {showForm && (
        <div className={slots.wrap()}>
          <Image
            alt={brandImage.alt}
            width={486}
            height={532}
            src={brandImage.src}
            className={slots.brandImage()}
          />
          <div className={slots.content()}>
            <div className={slots.title()}>
              {t('section.subscription.subscribeTitle', {
                count: discountPercentage
              })}
            </div>
            <div className={slots.formWrap()}>
              <div className={slots.form()}>
                <UiKitInputBase
                  label={t('form.label.email')}
                  name="email"
                  value={email}
                  onChange={onChangeEmail}
                  error={formStatus === 'error'}
                  helperText={formStatus === 'error' ? errorText : ''}
                />
                <UiKitButtonBase
                  className={slots.button()}
                  onClick={onClickSubscribe}
                >
                  {t('subscribe')}
                </UiKitButtonBase>
              </div>
              <div className="flex flex-col gap-3">
                <UiKitCheckboxBase
                  classNameInner={slots.disclaimer()}
                  error={newsletterError}
                  helperText={
                    newsletterError
                      ? t(
                          'section.subscription.subscribeNewsletterApprovalError'
                        )
                      : ''
                  }
                  checked={newsletterApproved}
                  onChange={(checked) => {
                    setNewsletterApproved(checked);
                    if (checked) setNewsletterError(false);
                  }}
                  label={t.rich(
                    'section.subscription.subscribeNewsletterApproval',
                    {
                      linkConsent: (chunks) => (
                        <Link
                          className={slots.approvalLink()}
                          href="/legal/agreement-subscriber/"
                        >
                          {chunks}
                        </Link>
                      )
                    }
                  )}
                />
                <UiKitCheckboxBase
                  classNameInner={slots.disclaimer()}
                  error={personalDataError}
                  helperText={
                    personalDataError ? t('form.validationError.required') : ''
                  }
                  checked={personalDataApproved}
                  onChange={(checked) => {
                    setPersonalDataApproved(checked);
                    if (checked) setPersonalDataError(false);
                  }}
                  label={t.rich(
                    'section.subscription.subscribePersonalDataApproval',
                    {
                      linkConsent: (chunks) => (
                        <Link
                          className={slots.approvalLink()}
                          href="/legal/agreement-users/"
                        >
                          {chunks}
                        </Link>
                      ),
                      linkPolicy: (chunks) => (
                        <Link
                          className={slots.approvalLink()}
                          href="/legal/politika-konfidentsialnosti/"
                        >
                          {chunks}
                        </Link>
                      )
                    }
                  )}
                />
              </div>
            </div>
            <p className={slots.successMessage()}>
              {t('section.subscription.successMessage')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
